import { makeAutoObservable, action, observable } from 'mobx';
import { trackAddToCart } from '@/utils/tracking';

class WooCommerceStore {
  generalSettings = [];
  productSettings = [];
  cart = [];
  cartVersion = '1';
  isProductModalOpen = false;
  selectedProduct = null;
  isCartOpen = false;
  isMinOrderModalOpen = false;
  isShippingAreaCheckModalOpen = false;
  quantityError = null;

  cartSubscribers = [];
  lastModifiedItem = null;
  lastModificationType = null; // 'add', 'update', 'remove'

  constructor() {
    makeAutoObservable(this, {
      setProductSettings: action,
      setGeneralSettings: action,
      addToCart: action,
      removeFromCart: action,
      updateCartItemQuantity: action,
      loadCartFromLocalStorage: action,
      saveCartToLocalStorage: action,
      showProductModal: action,
      hideProductModal: action,
      setCart: action,
      toggleCart: action,
      cartSubscribers: false,
      lastModifiedItem: observable,
      lastModificationType: observable,
      isCartOpen: observable,
      isMinOrderModalOpen: observable,
      quantityError: observable,
    });

    this.loadCartFromLocalStorage();
  }

  showProductModal(product) {
    this.isProductModalOpen = true;
    this.selectedProduct = product;
  }

  hideProductModal() {
    this.isProductModalOpen = false;
    this.selectedProduct = null;
  }

  setProductSettings(settings) {
    this.productSettings = settings;
  }

  setGeneralSettings(settings) {
    this.generalSettings = settings;
  }

  get currencySymbol() {
    return this.generalSettings?.currency_symbol || '₪'; // Default to ILS symbol
  }

  get weightUnit() {
    return this.generalSettings?.weight_unit || 'kg'; // Default to kg
  }

  get currency() {
    return this.generalSettings?.currency || 'ILS'; // Default to ILS
  }

  get currencyPos() {
    return this.generalSettings?.currency_pos || 'left';
  }

  get numDecimals() {
    return this.generalSettings?.price_num_decimals || 2;
  }

  get decimalSep() {
    return this.generalSettings?.price_decimal_sep || '.';
  }

  get thousandSep() {
    return this.generalSettings?.price_thousand_sep || ',';
  }

  // Cart Management
  addToCart(product, quantity = 1) {
    const existingItem = this.cart.find(item => item.id === product.id);
    const maxQuantity = product.max_cart_quantity ? parseInt(product.max_cart_quantity) : null;

    if (maxQuantity !== null) {
      const newTotalQuantity = (existingItem?.quantity || 0) + quantity;
      if (newTotalQuantity > maxQuantity) {
        this.quantityError = { key: 'max_quantity_error', values: { quantity: maxQuantity } };
        setTimeout(() => {
          this.quantityError = null;
        }, 3000);
        return false;
      }
    }

    if (existingItem) {
      existingItem.quantity += quantity;
      this.lastModifiedItem = { ...existingItem };
      this.lastModificationType = 'update';
    } else {
      const newItem = { ...product, quantity };
      this.cart.push(newItem);
      this.lastModifiedItem = newItem;
      this.lastModificationType = 'add';

      // Track add to cart event
      trackAddToCart(product, quantity);

      // If this is the first item and user is not logged in and hasn't checked shipping area before
      if (this.cart.length === 1 &&
          !localStorage.getItem('token') &&
          !localStorage.getItem('shipping_area_checked')) {
        this.toggleShippingAreaCheckModal();
      }
    }
    this.saveCartToLocalStorage();
    return true;
  }

  removeFromCart(productId) {
    const item = this.cart.find(item => item.id === productId);
    if (item) {
      this.lastModifiedItem = { ...item };
      this.lastModificationType = 'remove';
    }
    this.cart = this.cart.filter(item => item.id !== productId);
    this.saveCartToLocalStorage();
  }

  updateCartItemQuantity(productId, quantity) {
    const item = this.cart.find(item => item.id === productId);
    if (item) {
      const maxQuantity = item.max_cart_quantity ? parseInt(item.max_cart_quantity) : null;

      if (maxQuantity !== null && quantity > maxQuantity) {
        this.quantityError = { key: 'max_quantity_error', values: { quantity: maxQuantity } };
        setTimeout(() => {
          this.quantityError = null;
        }, 3000);
        return false;
      }

      item.quantity = quantity;
      if (item.quantity <= 0) {
        this.removeFromCart(productId);
      } else {
        this.lastModifiedItem = { ...item };
        this.lastModificationType = 'update';
        this.saveCartToLocalStorage();
      }
      return true;
    }
    return false;
  }

  updateCartItemNote(productId, note) {
    const item = this.cart.find(item => item.id === productId);
    if (item) {
      item.note = note;
      this.lastModifiedItem = { ...item };
      this.lastModificationType = 'update';
      this.saveCartToLocalStorage();
    }
  }

  clearCart() {
    this.cart = [];
    this.lastModifiedItem = null;
    this.lastModificationType = 'clear';
    this.saveCartToLocalStorage();
  }

  loadCartFromLocalStorage() {
    if (typeof window !== "undefined") {
      const savedCart = JSON.parse(localStorage.getItem('cart')) || [];
      this.cart = savedCart;
    }
  }

  saveCartToLocalStorage() {
    if (typeof window !== "undefined") {
      const sortedCart = this.cart.sort((a, b) => {
        // Get the first category name for each item
        const categoryA = a.categories && a.categories.length > 0 ? a.categories[0].name.toLowerCase() : '';
        const categoryB = b.categories && b.categories.length > 0 ? b.categories[0].name.toLowerCase() : '';

        // Compare category names
        if (categoryA < categoryB) return -1;
        if (categoryA > categoryB) return 1;
        return 0;
      });
      localStorage.setItem('cart', JSON.stringify(sortedCart));
      this.cartVersion = Math.floor(Math.random() * 1000) + 1;

      // Notify subscribers
      this.cartSubscribers.forEach(callback => callback());
    }
  }

  subscribeToCart(callback) {
    this.cartSubscribers.push(callback);
    return () => {
      const index = this.cartSubscribers.indexOf(callback);
      if (index > -1) {
        this.cartSubscribers.splice(index, 1);
      }
    };
  }

  getCartTotal() {
    return this.cart.reduce((total, item) => total + item.price * item.quantity, 0);
  }

  getCartItemCount() {
    return this.cart.reduce((total, item) => total + item.quantity, 0);
  }

  setCart(newCart) {
    this.cart = newCart;
    this.saveCartToLocalStorage();
  }

  toggleCart = () => {
    this.isCartOpen = !this.isCartOpen;
  }

  toggleMinOrderModal = () => {
    this.isMinOrderModalOpen = !this.isMinOrderModalOpen;
  }

  toggleShippingAreaCheckModal = () => {
    this.isShippingAreaCheckModalOpen = !this.isShippingAreaCheckModalOpen;
  }
}

const wooCommerceStore = new WooCommerceStore();
export default wooCommerceStore;
